import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

type Tier = 'red' | 'bronze' | 'silver' | 'gold' | 'diamond';
type Platform = 'ios' | 'android';

type MilestoneBadge = {
  tier: Tier;
  platform: Platform;
  url: string | null;
  fileKey?: string;
  updatedAt?: string;
  updatedBy?: string;
};

const TIERS: { key: Tier; label: string }[] = [
  { key: 'red', label: 'Red (first like of the session)' },
  { key: 'bronze', label: 'Bronze (101–250 likes)' },
  { key: 'silver', label: 'Silver (251–500 likes)' },
  { key: 'gold', label: 'Gold (501–1000 likes)' },
  { key: 'diamond', label: 'Diamond (1001+ likes)' },
];

const PLATFORMS: { key: Platform; label: string; contentType: string; accept: string; extLabel: string }[] = [
  { key: 'ios', label: 'iOS', contentType: 'video/quicktime', accept: '.mov,video/quicktime', extLabel: '.mov' },
  { key: 'android', label: 'Android', contentType: 'video/webm', accept: '.webm,video/webm', extLabel: '.webm' },
];

const badgeMapKey = (tier: Tier, platform: Platform) => `${tier}:${platform}`;

const LiveMilestoneBadges: React.FC = () => {
  const [badges, setBadges] = useState<Record<string, MilestoneBadge>>({});
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { toast.error('No token found. Please log in.'); return null; }
    if (!API_KEY) { toast.error('Missing API key.'); return null; }
    return getApiHeaders(token);
  }, []);

  const fetchBadges = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await fetch(`${walletApiBase}/getMilestoneBadges`, { method: 'POST', headers, body: '{}' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load badges');
      const map: Record<string, MilestoneBadge> = {};
      (json.data || []).forEach((b: MilestoneBadge) => { map[badgeMapKey(b.tier, b.platform)] = b; });
      setBadges(map);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [headerWithAuth]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const handleUpload = async (tier: Tier, platform: Platform, file: File) => {
    const headers = headerWithAuth();
    if (!headers) return;
    const platformConfig = PLATFORMS.find((p) => p.key === platform)!;
    const mapKey = badgeMapKey(tier, platform);
    setUploadingKey(mapKey);
    try {
      // Step 1: Get presigned URL for this tier+platform slot (deterministic
      // key — overwrites whatever was previously uploaded for that slot).
      const presignRes = await fetch(`${walletApiBase}/presignMilestoneBadgeUpload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Tier: tier, Platform: platform, ContentType: platformConfig.contentType }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const { FileKey, FileUrl, UploadUrl } = presignJson.data;

      // Step 2: Upload directly to R2
      const fileBuffer = await file.arrayBuffer();
      const uploadRes = await fetch(UploadUrl, {
        method: 'PUT',
        body: fileBuffer,
        headers: { 'Content-Type': platformConfig.contentType },
      });
      if (!uploadRes.ok) throw new Error('Failed to upload file to storage');

      // Step 3: Persist the new URL so tezda-ws picks it up on the next
      // LIKE_MILESTONE for this tier (Redis-backed, no redeploy needed).
      const saveRes = await fetch(`${walletApiBase}/saveMilestoneBadge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Tier: tier, Platform: platform, FileKey, FileUrl }),
      });
      const saveJson = await saveRes.json();
      if (!saveRes.ok) throw new Error(saveJson.message || 'Failed to save badge');

      toast.success(`${platformConfig.label} ${tier} badge updated`);
      setBadges((prev) => ({ ...prev, [mapKey]: saveJson.data }));
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploadingKey('');
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='mb-8'>
          <h1 className='text-2xl sm:text-3xl font-bold text-white'>Live Milestone Badges</h1>
          <p className='text-sm text-gray-400'>
            Celebration animation played when a viewer's likes cross a tier boundary during a livestream.
            Upload separately for iOS (.mov) and Android (.webm) — each platform plays the format it can
            render natively. Re-uploading a tier replaces the file everyone sees; no deploy needed.
          </p>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Loading current badges...</div>
        ) : (
          <div className='space-y-10'>
            {PLATFORMS.map((platform) => (
              <div key={platform.key}>
                <h2 className='text-lg font-semibold text-white mb-4'>{platform.label}</h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
                  {TIERS.map((tier) => {
                    const mapKey = badgeMapKey(tier.key, platform.key);
                    const badge = badges[mapKey];
                    const isUploading = uploadingKey === mapKey;
                    return (
                      <div
                        key={mapKey}
                        className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg'
                      >
                        <div className='h-40 bg-black/20 flex items-center justify-center'>
                          {badge?.url ? (
                            <video
                              src={badge.url}
                              className='h-full w-full object-contain'
                              muted
                              loop
                              autoPlay
                              playsInline
                            />
                          ) : (
                            <span className='text-xs text-gray-500'>No badge uploaded</span>
                          )}
                        </div>
                        <div className='p-4 space-y-2'>
                          <div className='text-sm font-semibold text-white capitalize'>{tier.key}</div>
                          <div className='text-xs text-gray-500'>{tier.label}</div>
                          {badge?.url ? (
                            <a
                              href={badge.url}
                              target='_blank'
                              rel='noreferrer'
                              className='block text-xs text-[#FF9C15] hover:underline break-all'
                            >
                              {badge.url}
                            </a>
                          ) : (
                            <div className='text-xs text-gray-600'>
                              {tier.key === 'red'
                                ? 'No animation set — milestone fires with no URL until uploaded'
                                : 'Falls back to the default heart animation'}
                            </div>
                          )}
                          <label
                            className={`mt-2 block w-full text-center px-3 py-2 text-xs font-semibold rounded-lg cursor-pointer transition ${
                              isUploading
                                ? 'bg-white/10 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90'
                            }`}
                          >
                            {isUploading ? 'Uploading...' : badge?.url ? `Replace ${platform.extLabel}` : `Upload ${platform.extLabel}`}
                            <input
                              type='file'
                              accept={platform.accept}
                              className='hidden'
                              disabled={isUploading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) handleUpload(tier.key, platform.key, file);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveMilestoneBadges;
